/**
 * Print Bridge client — talks to the local "DYYNIT Print Bridge" running on
 * the shop PC. The bridge forwards print jobs to BarTender via bartend.exe.
 *
 * The bridge listens on http://localhost:9100. Modern Chromium browsers
 * allow https://app.dyynit.com → http://localhost requests (localhost is
 * a "secure context"), so no mixed-content blocking.
 *
 * Exposed:
 *   - BRIDGE_BASE                 base URL ("http://localhost:9100")
 *   - getBridgeBase()/setBridgeBase()  override via localStorage
 *   - checkBridge()               GET /health — used as a connection probe
 *   - getBridgeConfig()/setBridgeConfig()
 *   - sendPrint(rows, opts)       POST /print → returns server JSON
 */

const LS_KEY = "dyynit_print_bridge_base";
const DEFAULT_BASE = "http://localhost:9100";

/**
 * SECURITY NOTE: This localStorage usage is intentionally safe.
 * 
 * We ONLY store the print bridge base URL (e.g., "http://localhost:9100"),
 * which is non-sensitive UI configuration. This allows users to customize
 * the bridge endpoint without affecting security.
 * 
 * NO sensitive data (tokens, credentials, PII) is stored here.
 * Authentication tokens are managed via httpOnly cookies in api.js.
 */
export function getBridgeBase() {
  try { return localStorage.getItem(LS_KEY) || DEFAULT_BASE; }
  catch (_) { return DEFAULT_BASE; }
}
export function setBridgeBase(url) {
  try { localStorage.setItem(LS_KEY, url || DEFAULT_BASE); }
  catch (_) { /* ignore — private mode / disabled storage */ }
}

async function _json(url, init) {
  // 2.5s timeout — health probes shouldn't hang the UI when bridge is offline.
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), init?.timeout ?? 8000);
  try {
    const res = await fetch(url, { ...init, signal: ctrl.signal });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data?.error || `HTTP ${res.status}`);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  } finally {
    clearTimeout(t);
  }
}

export async function checkBridge() {
  return _json(`${getBridgeBase()}/health`, { timeout: 2500 });
}

export async function getBridgeConfig() {
  return _json(`${getBridgeBase()}/config`, { timeout: 4000 });
}

export async function setBridgeConfig(patch) {
  return _json(`${getBridgeBase()}/config`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch || {}),
    timeout: 4000,
  });
}

/**
 * Send a print job to BarTender via the bridge.
 *
 * One row in `rows` == one printed label. The bridge does NOT multiply
 * by any copy count — callers must pre-expand their data.
 *
 * @param {Array<{barcode:string, mrp?:any, dp?:any}>} rows
 * @param {Object} [opts]
 * @param {string} [opts.printer_name]
 * @returns {Promise<{ok:true, rows:number, csv_path:string, btw_path:string, command:string}>}
 */
export async function sendPrint(rows, opts = {}) {
  return _json(`${getBridgeBase()}/print`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      rows: rows || [],
      printer_name: opts.printer_name || undefined,
    }),
    timeout: 120_000, // BarTender print can take up to ~2 min for large jobs
  });
}
